import * as yup from "yup";
import {MILLIMETRE_UNIT} from "@/constants";
import {
    Country,
    NumberOfPallet,
    OrderDataDTO,
    PalletType,
    PrintType,
    QuantityOptionDTO,
    RequestDTO
} from "@/api/data-contracts";
import {convertDate, formatCurrency, formatInteger} from "@/lib/utils";
import dayjs from "dayjs";
import {useMemo} from "react";
import {getI18n, useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {countryOptions, numberOfPalletLabels, palletTypeOptions, printOptions} from "@/datacaches";
import {Constants} from "@/api/constants";
import {CompanyI18N, RequestI18N} from "@/translations";
import {DateFormatter} from "@/lib/components/DateFormate";
import {ZIP_CODE_REGEX} from "@/regex";

export type RequestValidation = Pick<
    RequestDTO,
    "customerReferenceNumber" | "deliveryAddress" | "additionalInformation" | "validUntil" | "orderData" | "company"
> & {
    additionalInformation?: string | null;
    orderData?: Pick<
        OrderDataDTO,
        | "deliveryDateFrom"
        | "deliveryDateTo"
        | "cartonGrade"
        | "fefcoCode"
        | "palletType"
        | "internalDimensionLength"
        | "internalDimensionWidth"
        | "internalDimensionHeight"
        | "externalPrint"
        | "externalNumberOfColors"
        | "internalPrint"
        | "internalNumberOfColors"
        | "siliconeTape"
        | "tearTape"
        | "palletLabelNeutral"
        | "stretching"
        | "glued"
        | "stitched"
        | "taped"
        | "counterPallets"
        | "palletOverhang"
        | "allowPalletOverhang"
        | "palletizingFixed"
        | "palletHeight"
        | "numberOfPalletLabel"
        | "clicheCosts"
        | "toolingCosts"
        | "quantityOptions"
        | "paymentTerms"
        | "rfi"
        | "seriesOrder"
        | "seriesTotalQuantity"
    > & {
        quantityOptions?: Pick<QuantityOptionDTO, "quantity" | "netPricePer1000">[];
    };
};

/**
 * Collects duplicate quantity validation errors from a list of quantity options.
 * For each quantity value that appears more than once, creates a validation error
 * for each duplicate entry using the provided error factory.
 *
 * @param value - Array of quantity option objects to check for duplicates.
 * @param createError - Factory function that creates a ValidationError for a given index and quantity.
 * @returns Array of ValidationError instances for all duplicate entries.
 */
function collectDuplicateQuantityErrors(
    value: { quantity?: number | null }[],
    createError: (index: number, quantity: number) => yup.ValidationError
): yup.ValidationError[] {
    const duplicates: Record<number, number[]> = {};
    for (const [index, item] of value.entries()) {
        if (item.quantity != null) {
            if (duplicates[item.quantity]) {
                duplicates[item.quantity].push(index);
            } else {
                duplicates[item.quantity] = [index];
            }
        }
    }

    const errors: yup.ValidationError[] = [];
    const nonUniqueQuantities = Object.entries(duplicates).filter(([, indices]) => indices.length > 1);
    for (const [quantity, indices] of nonUniqueQuantities) {
        for (const index of indices) {
            errors.push(createError(index, Number(quantity)));
        }
    }
    return errors;
}

/**
 * Builds the three date field Yup schemas (validUntil, deliveryDateFrom, deliveryDateTo)
 * with shared base validation and optional minimum date constraints.
 * Used by both useRequestSchema and useExtendDatesSchema to avoid duplication.
 *
 * @param t - Translation function.
 * @param minDates - Optional minimum date values; when provided, the user cannot select an earlier date.
 * @returns An object containing the three Yup date schemas.
 */
function buildDateFieldSchemas(t: TFunction, minDates?: ExtendDatesMinDates) {
    const validUntilMin = minDates?.validUntil ? dayjs(minDates.validUntil).toDate() : undefined;
    const deliveryDateFromMin = minDates?.deliveryDateFrom ? dayjs(minDates.deliveryDateFrom).toDate() : undefined;
    const deliveryDateToMin = minDates?.deliveryDateTo ? dayjs(minDates.deliveryDateTo).toDate() : undefined;
    // The dates below are interpolated into translated messages, so their format must follow the
    // display language too — a hardcoded DD.MM.YYYY would put a German date inside an English
    // sentence. Callers rebuild the schema whenever `t` changes, so this is re-read on a language
    // switch.
    const dateFormat = DateFormatter("date");

    let validUntilSchema = yup
        .date()
        .optional()
        .nullable()
        .transform(convertDate)
        .typeError(t(RequestI18N.form.validation.dateFormat))
        .required(t(RequestI18N.form.validation.requestValidityRequired));
    if (validUntilMin) {
        validUntilSchema = validUntilSchema.min(
            validUntilMin,
            t(RequestI18N.form.validation.deliveryDateOnOrAfter, {date: dayjs(validUntilMin).format(dateFormat)})
        );
    }

    let deliveryDateFromSchema = yup
        .date()
        .optional()
        .nullable()
        .transform(convertDate)
        .typeError(t(RequestI18N.form.validation.dateFormat))
        .required(t(RequestI18N.form.validation.deliveryDateFrom));
    if (deliveryDateFromMin) {
        deliveryDateFromSchema = deliveryDateFromSchema.min(
            deliveryDateFromMin,
            t(RequestI18N.form.validation.deliveryDateOnOrAfter, {date: dayjs(deliveryDateFromMin).format(dateFormat)})
        );
    }

    let deliveryDateToSchema = yup
        .date()
        .optional()
        .nullable()
        .transform(convertDate)
        .typeError(t(RequestI18N.form.validation.dateFormat))
        .required(t(RequestI18N.form.validation.deliveryDateTo))
        .when("deliveryDateFrom", {
            is: (val: unknown) => val != null,
            then: (schema) => schema.min(yup.ref("deliveryDateFrom"), t(RequestI18N.form.validation.deliveryDateTo))
        });
    if (deliveryDateToMin) {
        deliveryDateToSchema = deliveryDateToSchema.min(
            deliveryDateToMin,
            t(RequestI18N.form.validation.deliveryDateOnOrAfter, {date: dayjs(deliveryDateToMin).format(dateFormat)})
        );
    }

    return {validUntilSchema, deliveryDateFromSchema, deliveryDateToSchema};
}

export type ExtendDatesMinDates = {
    validUntil?: string | null;
    deliveryDateFrom?: string | null;
    deliveryDateTo?: string | null;
};

export const useRequestSchema = () => {
    const {t} = useTranslation();
    const locale = getI18n().language;
    return useMemo(() => {
        const validCountryOptions = countryOptions.filter((option): option is Country => option != null);
        const dateFieldSchemas = buildDateFieldSchemas(t);
        const schema = yup
            .object()
            .shape({
                customerReferenceNumber: yup
                    .string()
                    .required(t(RequestI18N.form.validation.quotationNumber))
                    .max(
                        Constants.CUSTOMER_REFERENCE_NUMBER_LENGTH_MAX,
                        t(RequestI18N.form.validation.requestNumberMaxLength, {
                            max: formatInteger(Constants.CUSTOMER_REFERENCE_NUMBER_LENGTH_MAX, locale)
                        })
                    ),
                contactPerson: yup.object().required(t(RequestI18N.form.validation.contactPerson)),
                company: yup.object().required(t(RequestI18N.form.validation.company)),
                deliveryAddress: yup
                    .object()
                    .nullable()
                    .optional()
                    .required(t(RequestI18N.form.validation.deliveryAddress))
                    .shape({
                        companyAddress: yup.boolean().nullable().optional(),
                        street: yup
                            .string()
                            .required(t(CompanyI18N.form.validation.streetRequired))
                            .min(
                                Constants.STREET_LENGTH_MIN,
                                t(CompanyI18N.form.validation.streetMin, {min: Constants.STREET_LENGTH_MIN})
                            )
                            .max(
                                Constants.STREET_LENGTH_MAX,
                                t(CompanyI18N.form.validation.streetMax, {max: Constants.STREET_LENGTH_MAX})
                            ),
                        houseNumber: yup
                            .string()
                            .required(t(CompanyI18N.form.validation.houseNumberRequired))
                            .min(
                                Constants.HOUSE_NUMBER_LENGTH_MIN,
                                t(CompanyI18N.form.validation.houseNumberMin, {
                                    min: Constants.HOUSE_NUMBER_LENGTH_MIN
                                })
                            )
                            .max(
                                Constants.HOUSE_NUMBER_LENGTH_MAX,
                                t(CompanyI18N.form.validation.houseNumberMax, {
                                    max: Constants.HOUSE_NUMBER_LENGTH_MAX
                                })
                            ),
                        zip: yup.string().when("companyAddress", {
                            is: false,
                            then: (schema) =>
                                schema
                                    .required(t(CompanyI18N.form.validation.postalCodeRequired))
                                    .min(
                                        Constants.ZIP_LENGTH_MIN,
                                        t(CompanyI18N.form.validation.postalCodeMin, {min: Constants.ZIP_LENGTH_MIN})
                                    )
                                    .max(
                                        Constants.ZIP_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.postalCodeMax, {max: Constants.ZIP_LENGTH_MAX})
                                    )
                                    .matches(ZIP_CODE_REGEX, t(CompanyI18N.form.validation.postalCodeInvalid)),
                            otherwise: (schema) => schema.optional().nullable()
                        }),
                        city: yup.string().when("companyAddress", {
                            is: false,
                            then: (schema) =>
                                schema
                                    .required(t(CompanyI18N.form.validation.cityRequired))
                                    .min(
                                        Constants.CITY_LENGTH_MIN,
                                        t(CompanyI18N.form.validation.cityMin, {min: Constants.CITY_LENGTH_MIN})
                                    )
                                    .max(
                                        Constants.CITY_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.cityMax, {max: Constants.CITY_LENGTH_MAX})
                                    ),
                            otherwise: (schema) => schema.optional().nullable()
                        }),
                        country: yup.mixed().when("companyAddress", {
                            is: false,
                            then: (schema) =>
                                schema
                                    .required(t(CompanyI18N.form.validation.countryRequired))
                                    .oneOf(validCountryOptions, t(CompanyI18N.form.validation.countryInvalid)),
                            otherwise: (schema) => schema.optional().nullable()
                        }),
                        deliveryInstruction: yup
                            .object()
                            .nullable()
                            .optional()
                            .shape({
                                mondayInstruction: yup
                                    .string()
                                    .optional()
                                    .nullable()
                                    .max(
                                        Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.deliveryInstructionsMax, {
                                            max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX
                                        })
                                    ),
                                tuesdayInstruction: yup
                                    .string()
                                    .optional()
                                    .nullable()
                                    .max(
                                        Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.deliveryInstructionsMax, {
                                            max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX
                                        })
                                    ),
                                wednesdayInstruction: yup
                                    .string()
                                    .optional()
                                    .nullable()
                                    .max(
                                        Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.deliveryInstructionsMax, {
                                            max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX
                                        })
                                    ),
                                thursdayInstruction: yup
                                    .string()
                                    .optional()
                                    .nullable()
                                    .max(
                                        Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.deliveryInstructionsMax, {
                                            max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX
                                        })
                                    ),
                                fridayInstruction: yup
                                    .string()
                                    .optional()
                                    .nullable()
                                    .max(
                                        Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.deliveryInstructionsMax, {
                                            max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX
                                        })
                                    ),
                                saturdayInstruction: yup
                                    .string()
                                    .optional()
                                    .nullable()
                                    .max(
                                        Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.deliveryInstructionsMax, {
                                            max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX
                                        })
                                    ),
                                sundayInstruction: yup
                                    .string()
                                    .optional()
                                    .nullable()
                                    .max(
                                        Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.deliveryInstructionsMax, {
                                            max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX
                                        })
                                    ),
                                arrivalNotification: yup
                                    .string()
                                    .optional()
                                    .nullable()
                                    .max(
                                        Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX,
                                        t(CompanyI18N.form.validation.arrivalNotificationMax, {
                                            max: Constants.DELIVERY_INSTRUCTION_AND_ADVICE_NOTE_LENGTH_MAX
                                        })
                                    ),
                                timeSlotBookingRequired: yup.boolean().optional().nullable()
                            })
                    }),
                hideDeliveryAddress: yup.boolean().optional().nullable(),
                additionalInformation: yup
                    .string()
                    .nullable()
                    .optional()
                    .max(
                        Constants.REQUEST_ADDITIONAL_INFORMATION_LENGTH_MAX,
                        t(RequestI18N.form.validation.additionalInformationMaxLength, {
                            max: formatInteger(Constants.REQUEST_ADDITIONAL_INFORMATION_LENGTH_MAX, locale)
                        })
                    ),
                validUntil: dateFieldSchemas.validUntilSchema,

                orderData: yup.object().shape({
                    rfi: yup.boolean().nullable().optional(),
                    deliveryDateFrom: dateFieldSchemas.deliveryDateFromSchema,
                    deliveryDateTo: dateFieldSchemas.deliveryDateToSchema,
                    paymentTerms: yup
                        .string()
                        .nullable()
                        .optional()
                        .max(
                            Constants.PAYMENT_TERMS_LENGTH_MAX,
                            t(RequestI18N.form.validation.maxCharactersLength, {
                                max: formatInteger(Constants.PAYMENT_TERMS_LENGTH_MAX, locale)
                            })
                        ),
                    cartonGrade: yup
                        .object()
                        .shape({
                            code: yup.string().required(t(RequestI18N.form.validation.variety))
                        })
                        .required(t(RequestI18N.form.validation.variety)),
                    fefcoCode: yup
                        .string()
                        .nullable()
                        .optional()
                        .required(t(RequestI18N.form.validation.fefcoCode))
                        .max(
                            Constants.FEFCO_CODE_LENGTH_MAX,
                            t(RequestI18N.form.validation.fefcoCodeMax, {
                                max: formatInteger(Constants.FEFCO_CODE_LENGTH_MAX, locale)
                            })
                        ),
                    palletType: yup
                        .mixed<PalletType>()
                        .optional()
                        .nullable()
                        .required(t(RequestI18N.form.validation.palletType))
                        .oneOf(palletTypeOptions),
                    internalDimensionLength: yup
                        .number()
                        .optional()
                        .nullable()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .required(t(RequestI18N.form.validation.lengthRequired))
                        .min(
                            Constants.INTERNAL_DIMENSION_MIN,
                            t(RequestI18N.form.validation.lengthMin, {
                                min: formatInteger(Constants.INTERNAL_DIMENSION_MIN, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .max(
                            Constants.INTERNAL_DIMENSION_MAX,
                            t(RequestI18N.form.validation.lengthMax, {
                                max: formatInteger(Constants.INTERNAL_DIMENSION_MAX, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        ),
                    internalDimensionWidth: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .required(t(RequestI18N.form.validation.widthRequired))
                        .min(
                            Constants.INTERNAL_DIMENSION_MIN,
                            t(RequestI18N.form.validation.widthMin, {
                                min: formatInteger(Constants.INTERNAL_DIMENSION_MIN, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .max(
                            Constants.INTERNAL_DIMENSION_MAX,
                            t(RequestI18N.form.validation.widthMax, {
                                max: formatInteger(Constants.INTERNAL_DIMENSION_MAX, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        ),
                    internalDimensionHeight: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .required(t(RequestI18N.form.validation.heightRequired))
                        .min(
                            Constants.INTERNAL_DIMENSION_MIN,
                            t(RequestI18N.form.validation.heightMin, {
                                min: formatInteger(Constants.INTERNAL_DIMENSION_MIN, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .max(
                            Constants.INTERNAL_DIMENSION_MAX,
                            t(RequestI18N.form.validation.heightMax, {
                                max: formatInteger(Constants.INTERNAL_DIMENSION_MAX, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        ),
                    externalPrint: yup
                        .mixed<PrintType>()
                        .oneOf(printOptions)
                        .nullable()
                        .optional()
                        .required(t(RequestI18N.form.validation.printRequired)),
                    externalNumberOfColors: yup
                        .number()
                        .nullable()
                        .optional()
                        .when(["externalPrint"], {
                            is: (externalPrint: PrintType) =>
                                externalPrint != null && externalPrint !== PrintType.NO_PRINT,
                            then: (schema) => schema.required(t(RequestI18N.form.validation.numberOfColorsRequired))
                        })
                        .min(
                            Constants.NUMBER_OF_COLORS_MIN,
                            t(RequestI18N.form.validation.numberOfColorsMin, {
                                min: formatInteger(Constants.NUMBER_OF_COLORS_MIN, locale)
                            })
                        )
                        .max(
                            Constants.NUMBER_OF_COLORS_MAX,
                            t(RequestI18N.form.validation.numberOfColorsMax, {
                                max: formatInteger(Constants.NUMBER_OF_COLORS_MAX, locale)
                            })
                        ),
                    internalPrint: yup
                        .mixed<PrintType>()
                        .oneOf(printOptions)
                        .nullable()
                        .optional()
                        .required(t(RequestI18N.form.validation.printRequired)),
                    internalNumberOfColors: yup
                        .number()
                        .nullable()
                        .optional()
                        .when(["internalPrint"], {
                            is: (internalPrint: PrintType) =>
                                internalPrint != null && internalPrint !== PrintType.NO_PRINT,
                            then: (schema) => schema.required(t(RequestI18N.form.validation.numberOfColorsRequired))
                        })
                        .min(
                            Constants.NUMBER_OF_COLORS_MIN,
                            t(RequestI18N.form.validation.numberOfColorsMin, {
                                min: formatInteger(Constants.NUMBER_OF_COLORS_MIN, locale)
                            })
                        )
                        .max(
                            Constants.NUMBER_OF_COLORS_MAX,
                            t(RequestI18N.form.validation.numberOfColorsMax, {
                                max: formatInteger(Constants.NUMBER_OF_COLORS_MAX, locale)
                            })
                        )
                        .integer(t(RequestI18N.form.validation.integerOnly)),
                    palletLabelNeutral: yup.boolean().nullable().optional(),
                    stretching: yup.boolean().nullable().optional(),
                    glued: yup.boolean().nullable().optional(),
                    stitched: yup.boolean().nullable().optional(),
                    taped: yup.boolean().nullable().optional(),
                    siliconeTape: yup.boolean().nullable().optional(),
                    tearTape: yup.boolean().nullable().optional(),
                    counterPallets: yup.boolean().nullable().optional(),
                    palletExchange: yup.boolean().nullable().optional(),
                    allowPalletOverhang: yup.boolean().nullable().optional(),
                    palletizingFixed: yup.boolean().nullable().optional(),
                    palletOverhang: yup
                        .number()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .nullable()
                        .optional()
                        .when(["allowPalletOverhang"], {
                            is: (allowPalletOverhang: boolean) => allowPalletOverhang,
                            then: (schema) => schema.required(t(RequestI18N.form.validation.palletOverhangRequired))
                        })
                        .min(
                            Constants.PALLET_OVERHANG_MIN,
                            t(RequestI18N.form.validation.palletOverhangMin, {
                                min: formatInteger(Constants.PALLET_OVERHANG_MIN, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .max(
                            Constants.PALLET_OVERHANG_MAX,
                            t(RequestI18N.form.validation.palletOverhangMax, {
                                max: formatInteger(Constants.PALLET_OVERHANG_MAX, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .integer(t(RequestI18N.form.validation.integerOnly)),
                    palletHeight: yup
                        .number()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .nullable()
                        .optional()
                        .required(t(RequestI18N.form.validation.palletHeightRequired))
                        .min(
                            Constants.PALLET_HEIGHT_MIN,
                            t(RequestI18N.form.validation.palletHeightMin, {
                                min: formatInteger(Constants.PALLET_HEIGHT_MIN, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .max(
                            Constants.PALLET_HEIGHT_MAX,
                            t(RequestI18N.form.validation.palletHeightMax, {
                                max: formatInteger(Constants.PALLET_HEIGHT_MAX, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .integer(t(RequestI18N.form.validation.integerOnly)),
                    numberOfPalletLabel: yup
                        .mixed<NumberOfPallet>()
                        .oneOf(numberOfPalletLabels)
                        .nullable()
                        .optional()
                        .required(t(RequestI18N.form.validation.numberOfPallet)),
                    pricePerPallet: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .min(
                            Constants.PRICE_PER_PALLET_MIN,
                            t(RequestI18N.form.validation.pricePerPalletMin, {
                                min: formatInteger(Constants.PRICE_PER_PALLET_MIN, locale),
                                unit: "€"
                            })
                        )
                        .max(
                            Constants.PRICE_PER_PALLET_MAX,
                            t(RequestI18N.form.validation.pricePerPalletMax, {
                                max: formatInteger(Constants.PRICE_PER_PALLET_MAX, locale),
                                unit: "€"
                            })
                        ),
                    quantityPerPallet: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .min(
                            Constants.QUANTITY_PER_PALLET_MIN,
                            t(RequestI18N.form.validation.quantityPerPalletMin, {
                                min: formatInteger(Constants.QUANTITY_PER_PALLET_MIN, locale),
                                unit: "Stück"
                            })
                        )
                        .max(
                            Constants.QUANTITY_PER_PALLET_MAX,
                            t(RequestI18N.form.validation.quantityPerPalletMax, {
                                max: formatInteger(Constants.QUANTITY_PER_PALLET_MAX, locale),
                                unit: "Stück"
                            })
                        ),
                    bundlesPerPackage: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .min(
                            Constants.BUNDLES_PER_PACKAGE_MIN,
                            t(RequestI18N.form.validation.bundlesPerPackageMin, {
                                min: formatInteger(Constants.BUNDLES_PER_PACKAGE_MIN, locale),
                                unit: "Stück"
                            })
                        )
                        .max(
                            Constants.BUNDLES_PER_PACKAGE_MAX,
                            t(RequestI18N.form.validation.bundlesPerPackageMax, {
                                max: formatInteger(Constants.BUNDLES_PER_PACKAGE_MAX, locale),
                                unit: "Stück"
                            })
                        ),
                    quantityOptions: yup
                        .array(
                            yup
                                .object()
                                .defined()
                                .shape({
                                    quantity: yup
                                        .number()
                                        .typeError(t(RequestI18N.form.validation.integerOnly))
                                        .nullable()
                                        .optional()
                                        .required(t(RequestI18N.form.validation.quantityRequired))
                                        .min(
                                            Constants.QUANTITY_MIN,
                                            t(RequestI18N.form.validation.quantityMin, {
                                                min: formatInteger(Constants.QUANTITY_MIN, locale)
                                            })
                                        )
                                        .max(
                                            Constants.QUANTITY_MAX,
                                            t(RequestI18N.form.validation.quantityMax, {
                                                max: formatInteger(Constants.QUANTITY_MAX, locale)
                                            })
                                        ),
                                    netPricePer1000: yup
                                        .number()
                                        .nullable()
                                        .optional()
                                        .test(
                                            "min",
                                            t(RequestI18N.form.validation.netPriceMin, {
                                                minCurrency: formatCurrency(Constants.NET_PRICE_PER_1000_MIN, locale)
                                            }),
                                            (value) => {
                                                if (value === null || value === undefined) {
                                                    return true;
                                                }
                                                return value >= Constants.NET_PRICE_PER_1000_MIN;
                                            }
                                        )
                                        .test(
                                            "max",
                                            t(RequestI18N.form.validation.netPriceMax, {
                                                maxCurrency: formatCurrency(Constants.NET_PRICE_PER_1000_MAX, locale)
                                            }),
                                            (value) => {
                                                if (value === null || value === undefined) {
                                                    return true;
                                                }
                                                return value <= Constants.NET_PRICE_PER_1000_MAX;
                                            }
                                        )
                                })
                        )
                        .defined()
                        .test("uniqueQuantitiesError", function (value) {
                            if (!value) {
                                return true;
                            }
                            const errors = collectDuplicateQuantityErrors(value, (path, quantity) =>
                                this.createError({
                                    path: `${this.path}[${path}].quantity`,
                                    message: t(RequestI18N.form.validation.duplicateQuantity, {quantity})
                                })
                            );
                            if (errors.length > 0) {
                                throw new yup.ValidationError(errors);
                            }
                            return true;
                        }),

                    toolingCosts: yup
                        .number()
                        .nullable()
                        .optional()
                        .test(
                            "min",
                            t(RequestI18N.form.validation.toolingCostsMin, {
                                minCurrency: formatCurrency(Constants.TOOLING_COSTS_PRICE_MIN, locale)
                            }),
                            (value) => {
                                if (value === null || value === undefined) {
                                    return true;
                                }
                                return value >= Constants.TOOLING_COSTS_PRICE_MIN;
                            }
                        )
                        .test(
                            "max",
                            t(RequestI18N.form.validation.toolingCostsMax, {
                                maxCurrency: formatCurrency(Constants.TOOLING_COSTS_PRICE_MAX, locale)
                            }),
                            (value) => {
                                if (value === null || value === undefined) {
                                    return true;
                                }
                                return value <= Constants.TOOLING_COSTS_PRICE_MAX;
                            }
                        ),
                    clicheCosts: yup
                        .number()
                        .nullable()
                        .optional()
                        .test(
                            "min",
                            t(RequestI18N.form.validation.clicheCostsMin, {
                                minCurrency: formatCurrency(Constants.CLICHE_COSTS_PRICE_MIN, locale)
                            }),
                            (value) => {
                                if (value === null || value === undefined) {
                                    return true;
                                }
                                return value >= Constants.CLICHE_COSTS_PRICE_MIN;
                            }
                        )
                        .test(
                            "max",
                            t(RequestI18N.form.validation.clicheCostsMax, {
                                maxCurrency: formatCurrency(Constants.CLICHE_COSTS_PRICE_MAX, locale)
                            }),
                            (value) => {
                                if (value === null || value === undefined) {
                                    return true;
                                }
                                return value <= Constants.CLICHE_COSTS_PRICE_MAX;
                            }
                        ),
                    seriesOrder: yup.boolean().nullable().optional().default(false),
                    seriesTotalQuantity: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .when(["seriesOrder"], {
                            is: (seriesOrder: boolean) => seriesOrder,
                            then: (schema) =>
                                schema
                                    .required(t(RequestI18N.form.validation.seriesTotalQuantityRequired))
                                    .min(
                                        Constants.SERIES_TOTAL_QUANTITY_MIN,
                                        t(RequestI18N.form.validation.seriesTotalQuantityMin, {
                                            min: formatInteger(Constants.SERIES_TOTAL_QUANTITY_MIN, locale)
                                        })
                                    )
                                    .max(
                                        Constants.SERIES_TOTAL_QUANTITY_MAX,
                                        t(RequestI18N.form.validation.seriesTotalQuantityMax, {
                                            max: formatInteger(Constants.SERIES_TOTAL_QUANTITY_MAX, locale)
                                        })
                                    )
                                    .integer(t(RequestI18N.form.validation.integerOnly)),
                            otherwise: (schema) => schema.notRequired()
                        })
                })
            })
            .stripUnknown();

        return schema as unknown as yup.ObjectSchema<RequestDTO>;
    }, [locale, t]);
};

/**
 * Returns a Yup schema for the three date extension fields.
 * Applies the same base validation as the main request schema,
 * plus a minimum date constraint based on the currently set values.
 * Also validates that deliveryDateTo is not before deliveryDateFrom.
 *
 * @param minDates - The current date values; each field must not be set to an earlier date.
 * @returns A Yup ObjectSchema for { validUntil, deliveryDateFrom, deliveryDateTo }.
 */
export const useExtendDatesSchema = (minDates: ExtendDatesMinDates) => {
    const {t} = useTranslation();
    return useMemo(() => {
        const {validUntilSchema, deliveryDateFromSchema, deliveryDateToSchema} = buildDateFieldSchemas(t, minDates);
        return yup.object().shape({
            validUntil: validUntilSchema,
            deliveryDateFrom: deliveryDateFromSchema,
            deliveryDateTo: deliveryDateToSchema
        });
    }, [t, minDates]);
};
